// src/components/forms/variants/split/types.ts

import type { 
  BaseFormProps, 
  ContentConfig, 
  ThemeConfig,
  ImageConfig,
  FloatingContentConfig,
  FeatureListItemConfig,
  PrivacyConfig,
  BadgeConfig,
  SubtitleConfig
} from '~/components/forms/base/types'
import { siteConfig } from '~/lib/config/site'

/**
 * Layout presets for the split email capture form
 */
export type SplitPresetLayout = 
  | 'modern' // Modern layout with feature descriptions and flexible content
  | 'classic' // Original layout with badge and simple feature list

/**
 * Props for the SplitEmailCapture component
 */
export interface SplitEmailCaptureProps extends Omit<BaseFormProps, 'theme' | 'content'> {
  /** Layout preset to use */
  preset?: SplitPresetLayout
  
  /** Image configuration including floating content */
  image: ImageConfig & {
    floatingContent?: FloatingContentConfig
  }
  
  /** Theme configuration extending base theme */
  theme?: ThemeConfig & {
    imagePosition?: 'left' | 'right'
    accentColor?: string
    textColor?: string
    backgroundColor?: string
  }
  
  /** Content configuration extending base content */
  content: ContentConfig & {
    badge?: BadgeConfig
    subtitle?: SubtitleConfig
    features?: FeatureListItemConfig[]
    privacy?: PrivacyConfig
  }
}

/**
 * Default preset configurations for different layout styles
 */
export const SPLIT_PRESETS: Record<SplitPresetLayout, Partial<SplitEmailCaptureProps>> = {
  classic: {
    theme: {
      variant: 'split',
      layout: 'split',
      size: 'lg',
      theme: 'light',
      imagePosition: 'right',
      className: {
        container: 'overflow-hidden rounded-2xl bg-white shadow-2xl',
        form: 'flex flex-col h-full'
      }
    },
    content: {
      input: {
        placeholder: 'Enter your work email',
        className: 'w-full px-4 py-4 text-base rounded-xl border-0 bg-gray-50 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-500'
      },
      button: {
        text: 'Get Started',
        loadingText: 'Please wait...',
        className: 'mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-4 text-base font-semibold text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50'
      },
      messages: {
        success: 'Thanks for signing up!',
        error: 'Something went wrong. Please try again.'
      },
      privacy: {
        enabled: true,
        text: 'By signing up, you agree to our',
        privacyLink: {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`
        },
        termsLink: {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`
        }
      }
    },
    validation: {
      rules: {
        required: true,
        format: true,
        minLength: 5,
        maxLength: 254,
        blockedDomains: ['temp.com', 'tempmail.com', 'disposable.com']
      },
      messages: {
        required: 'Please enter your email address',
        invalid: 'Please enter a valid email address',
        blocked: 'Please use a permanent email address'
      },
      timing: {
        validateOnChange: true,
        validateOnBlur: true,
        validateOnSubmit: true,
        debounceMs: 300
      }
    }
  },
  modern: {
    theme: {
      variant: 'split',
      layout: 'split',
      size: 'lg',
      theme: 'light',
      imagePosition: 'right',
      className: {
        container: 'overflow-hidden rounded-xl bg-white shadow-lg',
        form: 'flex flex-col h-full'
      }
    },
    content: {
      input: {
        placeholder: 'Enter your work email',
        className: 'w-full px-4 py-3 text-base rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500'
      },
      button: {
        text: 'Get Started',
        loadingText: 'Please wait...',
        className: 'mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
      },
      messages: {
        success: 'Thanks for signing up!',
        error: 'Something went wrong. Please try again.'
      },
      privacy: {
        enabled: true,
        text: 'By continuing, you agree to our',
        privacyLink: {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`
        },
        termsLink: {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`
        }
      }
    },
    validation: {
      rules: {
        required: true,
        format: true,
        minLength: 5,
        maxLength: 254,
        blockedDomains: ['temp.com', 'tempmail.com', 'disposable.com']
      },
      messages: {
        required: 'Please enter your email address',
        invalid: 'Please enter a valid email address',
        blocked: 'Please use a permanent email address'
      },
      timing: {
        validateOnChange: true,
        validateOnBlur: true,
        validateOnSubmit: true,
        debounceMs: 300
      }
    }
  }
} as const